import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { uc } from './unifiedContactsApi';
import { TimelineList } from './ContactTimelineList';
import { errorMessage } from './errors';

// Ticket 188 D9: the Activities tab. Chips pick the kinds the server returns, so
// paging ("Load older") walks only the kinds in view. Newest first: older rows
// are added at the bottom.
const PAGE = 50;
const CHIPS = [
  { key: 'all', label: 'All', kinds: null },
  { key: 'appointments', label: 'Appointments', kinds: ['appointment'] },
  { key: 'opportunities', label: 'Opportunities', kinds: ['opportunity_created', 'stage_moved'] },
  { key: 'tasks', label: 'Tasks', kinds: ['task'] },
  { key: 'notes', label: 'Notes', kinds: ['note'] },
  { key: 'loans', label: 'Loans', kinds: ['loan_file', 're_transaction'] },
  { key: 'texts', label: 'Texts', kinds: ['text_sent'] },
  { key: 'other', label: 'Other', kinds: ['contact_created', 'consent', 'lead'] },
];

export default function ContactActivity({ contactId }) {
  const [chip, setChip] = useState('all');
  const [state, setState] = useState({ loading: true, error: null, events: [], nextBefore: null });
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState('');
  // Drops an answer that lands after the chip or the contact changed.
  const request = useRef(0);

  const params = useCallback((extra = {}) => {
    const kinds = CHIPS.find((c) => c.key === chip)?.kinds;
    return { limit: PAGE, ...(kinds ? { kinds: kinds.join(',') } : {}), ...extra };
  }, [chip]);

  useEffect(() => {
    const mine = ++request.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    setOlderError('');
    uc.timeline(contactId, params())
      .then((d) => {
        if (request.current !== mine) return;
        setState({ loading: false, error: null, events: d.events || [], nextBefore: d.next_before || null });
      })
      .catch((e) => {
        if (request.current !== mine) return;
        setState({ loading: false, error: errorMessage(e), events: [], nextBefore: null });
      });
    return () => { request.current += 1; };
  }, [contactId, params]);

  const loadOlder = async () => {
    const mine = request.current;
    setLoadingOlder(true);
    setOlderError('');
    try {
      const d = await uc.timeline(contactId, params({ before: state.nextBefore }));
      if (request.current !== mine) return;
      setState((s) => ({ ...s, events: [...s.events, ...(d.events || [])], nextBefore: d.next_before || null }));
    } catch (e) {
      if (request.current === mine) setOlderError(errorMessage(e));
    } finally {
      setLoadingOlder(false);
    }
  };

  const { loading, error, events, nextBefore } = state;
  return (
    <div className="space-y-4" data-testid="contact-activity">
      <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="Filter by kind">
        {CHIPS.map((c) => (
          <button key={c.key} type="button" onClick={() => setChip(c.key)} aria-pressed={chip === c.key}
            data-testid={`activity-chip-${c.key}`}
            className={`px-3 py-1 rounded-full text-xs border ${chip === c.key
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
            {c.label}
          </button>
        ))}
      </div>
      {loading && (
        <div className="p-8 flex items-center justify-center text-slate-400" data-testid="activity-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading activity...
        </div>
      )}
      {!loading && error && (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center text-red-600 text-sm" data-testid="activity-error">
          {error}
        </div>
      )}
      {!loading && !error && (
        <>
          <TimelineList events={events} />
          {nextBefore && (
            <div className="text-center">
              <button type="button" onClick={loadOlder} disabled={loadingOlder} data-testid="activity-load-older"
                className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60">
                {loadingOlder ? 'Loading...' : 'Load older'}
              </button>
              {olderError && <p className="text-xs text-red-600 mt-1" role="alert">{olderError}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
